/**
 * Family & passes — permanent-pass member cards, add member sheet, kids
 * route through to their recurring schedule. Wired: GET/POST
 * /api/services/family-pass. Port of FamilyPass in
 * design/source/screens-services.jsx.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { bool, str, useGateList } from '@/components/gate/api';
import { InfoNote, Toggle } from '@/components/gate/bits';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, Field, IconButton, Input, Pill, Screen, SectionLabel, Sheet, Skeleton, TopBar } from '@/ui';

type Member = { id: string; name: string; relation: string; kid: boolean; passActive: boolean };

/** Arch-shaped avatar — kids get the one amber accent on this screen (design's
 * Kiara Sharma treatment); everyone else stays primary-tinted. */
function ArchAvatar({ name, size = 44, kid }: { name: string; size?: number; kid?: boolean }) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const bg = kid ? colors.accent100 : colors.primary100;
  const fg = kid ? colors.accent700 : colors.primary900;
  return (
    <View style={{
      width: size, height: size * 1.15,
      borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
      borderBottomLeftRadius: size * 0.11, borderBottomRightRadius: size * 0.11,
      backgroundColor: bg, alignItems: 'center', justifyContent: 'center',
    }}>
      <AppText weight="700" style={{ color: fg, fontSize: size * 0.28 }}>{initials}</AppText>
    </View>
  );
}

function toMember(m: unknown, i: number): Member {
  return {
    id: str(m, 'id') ?? `m${i}`,
    name: str(m, 'memberName', 'name', 'fullName') ?? 'Family member',
    relation: str(m, 'relation', 'rel') ?? '',
    kid: bool(m, 'isKid', 'kid', 'isChild') ?? false,
    passActive: bool(m, 'passActive', 'active', 'permanent') ?? true,
  };
}

export default function FamilyPassScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, setItems, state, reload } = useGateList<unknown>('/api/services/family-pass', 'family', 'members');
  const rows = items.map(toMember);

  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [name, setName] = React.useState('');
  const [relation, setRelation] = React.useState('');
  const [kid, setKid] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [notice, setNotice] = React.useState<string | null>(null);

  const addMember = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setNotice(null);
    try {
      await api.post<unknown>('/api/services/family-pass', { memberName: name.trim(), relation: relation.trim(), isKid: kid });
      setSheetOpen(false);
      setName('');
      setRelation('');
      setKid(false);
      await reload();
    } catch {
      setNotice(t('fp_add_error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('fp_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('fp_add')} onPress={() => setSheetOpen(true)}>
            <Icon name="plus" size={22} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s4 }}>
        <SectionLabel>{t('fp_permanent')}</SectionLabel>
        <InfoNote icon="info" style={{ marginBottom: space.s3 }}>
          {t('fp_note')}
        </InfoNote>

        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} h={76} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('fp_err_title') : t('fp_empty_title')}
            body={state === 'error' ? t('fp_err_body') : t('fp_empty_body')}
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {rows.map((m) => (
              <Card key={m.id} style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                <ArchAvatar name={m.name} size={44} kid={m.kid} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="500">{m.name}</AppText>
                  {m.relation ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{m.relation}</AppText> : null}
                </View>
                {m.kid ? (
                  <Button
                    title={t('fp_schedules')}
                    kind="secondary"
                    style={{ paddingVertical: space.s2, paddingHorizontal: space.s3, minHeight: 40 }}
                    onPress={() => router.push('/kids-permission')}
                  />
                ) : (
                  <Pill status={m.passActive ? 'approved' : 'pending'} label={m.passActive ? t('fp_pass_active') : t('fp_pending')} />
                )}
              </Card>
            ))}
          </View>
        )}
        {notice ? <AppText variant="bodySm" tone="danger" style={{ marginTop: space.s4 }}>{notice}</AppText> : null}
      </Screen>

      <Sheet visible={sheetOpen} onClose={() => setSheetOpen(false)}>
        <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>{t('fp_add')}</AppText>
        <View style={{ gap: space.s4 }}>
          <Field label={t('name_lbl')}>
            <Input value={name} onChangeText={setName} placeholder={t('full_name')} />
          </Field>
          <Field label={t('fp_relation')}>
            <Input value={relation} onChangeText={setRelation} placeholder={t('fp_relation_ph')} />
          </Field>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <AppText variant="bodyMd">{t('fp_is_kid')}</AppText>
            <Toggle on={kid} onChange={setKid} label={t('fp_is_kid_short')} />
          </View>
        </View>
        <Button title={t('fp_add_member')} size="lg" loading={saving} disabled={!name.trim()} onPress={() => void addMember()} style={{ marginTop: space.s5 }} />
      </Sheet>
    </Screen>
  );
}
